// Italian copy for the pricing pages ("tu", like the site's it locale). Same shape as copy.en.ts (the source of
// truth); keep the two in step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names and stay
// in English, as they do in the app. "FTP su richiesta", "l'uploader web" and "anteprima prima" as on
// /on-demand-ftp and /preview-first-uploads.

import type { PricingCopy } from "./copy.en";

const it: PricingCopy = {
  common: {
    perMonth: "/mese",
    onDemandFtpLink: "Come funziona l'FTP su richiesta",
    aMonth: "al mese",
    today: "Oggi",
    fromChangeDate: "Dal 1° novembre",
    changeDate: "1° novembre 2026",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "Il più scelto",
    renamedPlan: "{from} (ora si chiama {to})",
    planFeatures: {
      free: [
        {
          text: "Il tuo branding, “Powered by Photohawk”",
          note: "Il tuo logo e i tuoi colori in ogni galleria. Un piccolo badge “Powered by Photohawk” compare in fondo alle tue gallerie e alla pagina di accesso della tua area di lavoro. Pro e Max non hanno il badge.",
        },
        "Team fino a 3 persone",
        "Assistenza tramite il centro assistenza",
        {
          text: "Server FTP su richiesta",
          note: "Se l'FTP è indispensabile, avvia il tuo server dall'uploader web, con il tuo indirizzo Photohawk che non cambia mai. Si spegne automaticamente dopo 1 ora di inattività.",
        },
        {
          text: "Caricamenti con anteprima prima",
          note: "La galleria va online in pochi minuti con anteprime leggere mentre arrivano gli originali, sia per le vendite sia per i download gratuiti.",
        },
      ],
      pro: [
        {
          text: "Il tuo branding",
          note: "Il tuo logo e i tuoi colori, senza badge Photohawk.",
        },
        "Dominio personalizzato",
        "Team fino a 10 persone",
        "Statistiche visitatori",
        {
          text: "Server FTP su richiesta",
          note: "Se l'FTP è indispensabile, avvia il tuo server dall'uploader web, con il tuo indirizzo Photohawk che non cambia mai. Si spegne automaticamente dopo 1 ora di inattività.",
        },
        {
          text: "Caricamenti con anteprima prima",
          note: "La galleria va online in pochi minuti con anteprime leggere mentre arrivano gli originali, sia per le vendite sia per i download gratuiti.",
        },
        "Assistenza via email",
      ],
      max: [
        "Tutto ciò che c'è in Pro",
        "Caricamento di video",
        "Ripartizione dei ricavi",
        "Più marchi",
        "Membri del team e ruoli illimitati",
        "Statistiche visitatori",
        {
          text: "Server FTP su richiesta",
          note: "Se l'FTP è indispensabile, avvia il tuo server dall'uploader web, con il tuo indirizzo Photohawk che non cambia mai. Si spegne automaticamente dopo 1 ora di inattività.",
        },
        {
          text: "Caricamenti con anteprima prima",
          note: "La galleria va online in pochi minuti con anteprime leggere mentre arrivano gli originali, sia per le vendite sia per i download gratuiti.",
        },
        "Assistenza prioritaria",
      ],
    },
    planTaglines: {
      free: "Per iniziare e per eventi occasionali.",
      pro: "Per chi vende regolarmente con il proprio marchio.",
      max: "Per studi, team e grandi volumi: la commissione più bassa, più spazio, video e assistenza prioritaria.",
    },
    countries: {
      uk: "Regno Unito",
      my: "Malesia",
      ph: "Filippine",
      mx: "Messico",
      co: "Colombia",
      eur: "Portogallo e area euro",
      us: "Stati Uniti",
      ca: "Canada",
    },
    inCountry: {
      uk: "nel Regno Unito",
      my: "in Malesia",
      ph: "nelle Filippine",
      mx: "in Messico",
      co: "in Colombia",
      eur: "in Portogallo e nell'area euro",
      us: "negli Stati Uniti",
      ca: "in Canada",
    },
  },

  pricing: {
    seo: {
      title: "Prezzi | Photohawk",
      description:
        "Commissioni più basse con ogni piano per la fotografia di eventi: 16% con Lite, 14% con Pro e 12% con Max. Spazio di archiviazione e download gratuiti inclusi.",
    },
    notice: "Nuovi prezzi dal 1° novembre 2026.",
    noticeLink: "Scopri cosa cambia",
    hero: {
      badge: "Prezzi",
      title: "Commissioni più basse con ogni piano.",
      titleHighlight: "A partire da {rate}.",
      subtitle:
        "Vendi al 16% con Lite, al 14% con Pro o al 12% con Max. Le commissioni di transazione funzionano esattamente come oggi.",
      events: "Gli stessi piani per ogni tipo di evento: corse e pedalate, partite, spettacoli, festival, lauree e conferenze.",
    },
    rate: {
      heading: "La tua commissione con ogni piano",
      planLabel: "con {plan}",
      points: [
        "Le commissioni sulle carte sono a parte e funzionano esattamente come oggi.",
        "La nostra commissione è l'unico costo Photohawk su una vendita.",
        "Gli accordi personalizzati esistenti restano validi fino alla loro scadenza.",
      ],
    },
    plans: {
      heading: "Scegli il tuo piano",
      subtitle:
        "Piani mensili in dollari USA, senza vincoli. Ogni piano superiore abbassa la commissione e aggiunge spazio e download gratuiti.",
      sellingLabel: "Vendite",
      sellingLine: "{rate} di commissione sulle vendite",
      sellingNote: "Più le commissioni di transazione, come oggi",
      freeDownloadsLabel: "Download gratuiti",
      freeDownloadsLine: "{count} foto al mese incluse",
      overageLine: "poi $8 ogni 1000 foto",
      storageLabel: "Spazio",
      storageLine: "{storage} inclusi",
      storageNote: "poi pacchetti da $5 al mese",
      includedLabel: "Incluso",
      cta: "Inizia",
      ctaFor: "con {plan}",
      planChange:
        "Cambio di piano: la commissione più bassa di un piano si applica dal tuo primo mese intero con quel piano. I passaggi a un piano inferiore hanno effetto a fine mese.",
    },
    standard: {
      heading: "Inclusi in ogni piano",
      subtitle: "Ogni piano li include, anche Lite a $0 al mese.",
      items: [
        { id: "selfie", title: "Ricerca con selfie", body: "Gli acquirenti si fanno un selfie e vedono ogni foto in cui compaiono." },
        { id: "bib", title: "Ricerca per pettorale e orario", body: "Trova le foto per numero di pettorale o per l'ora dello scatto." },
        { id: "preview", title: "Protezione delle anteprime", body: "Protezione dagli screenshot con tocco per mostrare, così le anteprime vendono invece di essere copiate." },
        {
          id: "uploader",
          title: "Uploader web e Photohawk Studio",
          body: "Il modo standard per caricare: invia molte foto alla volta, si adatta alla tua connessione e riprende da solo. Con ogni piano, le anteprime leggere possono partire per prime, così la tua galleria è online in pochi minuti. Studio oggi funziona nel browser; l'app Photohawk Studio per Mac, iPhone e iPad arriva a novembre 2026.",
        },
        { id: "whatsapp", title: "Consegna via WhatsApp, un servizio Photohawk per gli acquirenti", body: "Al checkout gli acquirenti possono scegliere di pagare un piccolo extra per ricevere le foto su WhatsApp, pronte da condividere. Photohawk lo offre in ogni galleria: non devi configurare, prezzare o disattivare nulla." },
      ],
    },
    storage: {
      heading: "Spazio di archiviazione",
      subtitle:
        "Ogni piano include spazio di archiviazione. Se archivi di più e hai una carta registrata, i pacchetti si aggiungono in automatico: paghi la combinazione più economica che copre ciò che archivi. Nessuna carta registrata? I nuovi caricamenti vanno in pausa finché non ne aggiungi una; non viene eliminato nulla e le tue gallerie continuano a vendere.",
      includedHeading: "Incluso",
      packsHeading: "Pacchetti di spazio aggiuntivo",
      packPrice: "${price} al mese",
      examplesHeading: "Come si sommano i pacchetti",
      example: "{plan} con {stored} archiviati: {over} in più, fatturati come {mix} = ${price} al mese",
      note: "Ti scriviamo un'email quando raggiungi l'80% del tuo spazio. I pacchetti si tolgono quando liberi spazio.",
    },
    freeDownloads: {
      heading: "Gallerie con download gratuito",
      subtitle:
        "Condividi gallerie da cui i tuoi acquirenti scaricano gratis. Ogni piano include foto ogni mese; oltre, con ogni piano, si pagano $8 ogni 1000 foto ($0,008 a foto).",
      line: "{count} foto al mese",
      note: "La quota riparte ogni mese e le gallerie continuano a funzionare anche oltre.",
    },
    example: {
      heading: "Quanto ti resta",
      subtitle: "Un ordine nel Regno Unito pagato con carta, sul tuo account Stripe, con ogni piano. Prova un altro importo.",
      orderLabel: "Valore dell'ordine",
      order: "Ordine",
      cardFee: "Commissione Stripe sulla carta",
      cardFeeNote: "Addebitata da Stripe sul tuo account, ad es. carte del Regno Unito 1,5% + 20p",
      colPlan: "Piano",
      colCommission: "Commissione Photohawk",
      colKeep: "Ti restano circa",
      planCell: "{plan} ({rate})",
      note: "La commissione di Stripe dipende dalla carta: le carte internazionali e la conversione di valuta di solito costano di più. La nostra commissione è l'unico costo Photohawk.",
    },
    cardFees: {
      heading: "Come funzionano le commissioni sulle carte",
      intro: "Le commissioni sulle carte e di elaborazione dei pagamenti funzionano esattamente come oggi, separate dalla nostra commissione.",
      items: [
        {
          id: "stripe",
          title: "Sul tuo account Stripe",
          body: "Nel Regno Unito, nell'UE, negli Stati Uniti e negli altri paesi Stripe, Stripe addebita la sua commissione standard di elaborazione direttamente sul tuo account Stripe.",
        },
        {
          id: "managed",
          title: "Dove Photohawk elabora il pagamento per te",
          body: "In Malesia, nelle Filippine, in Messico, Colombia, Ecuador e negli altri paesi in cui gli acquirenti pagano con wallet locali, viene trattenuta una commissione di elaborazione del pagamento a tariffe locali basate sui costi, come oggi.",
        },
      ],
      linkLabel: "Vedi le commissioni sulle carte per paese",
    },
    compare: {
      heading: "Confronta tutte le funzioni",
      subtitle: "Tutto ciò che c'è in Photohawk Studio, piano per piano. Oggi Studio funziona nel browser con ogni piano.",
      featureColumn: "Funzione",
      included: "Incluso",
      notIncluded: "Non incluso",
      linkLabel: "Confronta tutte le funzioni",
      sections: {
        selling: "Vendite",
        finding: "Trovare le foto",
        galleries: "Gallerie",
        brand: "Il tuo marchio",
        insights: "Statistiche",
        team: "Team",
        uploading: "Caricamento",
        storage: "Spazio",
        support: "Assistenza",
      },
      rows: {
        commission: { label: "Commissione sulle vendite", note: "Il nostro unico costo su una vendita. Le commissioni sulle carte funzionano esattamente come oggi." },
        products: { label: "Download digitali, stampe e prodotti", note: "I tuoi prodotti e i tuoi prezzi." },
        packages: { label: "Pacchetti e bundle", note: "" },
        coupons: { label: "Coupon", note: "" },
        preorder: { label: "Prezzi di preordine", note: "" },
        whatsapp: { label: "Consegna via WhatsApp (un servizio Photohawk per gli acquirenti)", note: "Un extra facoltativo che gli acquirenti pagano al checkout, così le foto arrivano su WhatsApp pronte da condividere. Gestito da Photohawk in ogni galleria; non è un'impostazione che controlli tu." },
        payouts: { label: "Pagamenti", note: "Sul tuo account Stripe, oppure versati da Photohawk dove elaboriamo il pagamento per te." },
        revenueSplit: { label: "Ripartizione dei ricavi", note: "Condividi le vendite di una galleria con fotografi, squadre, club o sponsor. Photohawk calcola ogni quota e la paga per te." },
        selfie: { label: "Ricerca con selfie", note: "" },
        bib: { label: "Ricerca per pettorale e testo", note: "Trova le foto per numero di pettorale o per il testo nella foto." },
        time: { label: "Ricerca per orario", note: "" },
        find: { label: "Presenza in Trova le tue foto", note: "Gli acquirenti trovano le tue gallerie pubbliche su photohawk.com." },
        profiles: { label: "Ricerche salvate", note: "Salva un selfie, un numero o un testo e vedi le nuove corrispondenze." },
        faceData: { label: "Dati del volto per la ricerca con selfie", note: "Nessuna scadenza per impostazione predefinita, o un periodo a tua scelta, dal 1° novembre 2026." },
        galleries: { label: "Gallerie e collezioni", note: "Tutte quelle che ti servono." },
        private: { label: "Gallerie private e password", note: "" },
        protection: { label: "Protezione delle anteprime", note: "Protezione dagli screenshot con tocco per mostrare." },
        watermarks: { label: "Filigrane", note: "" },
        freeDownloads: { label: "Foto con download gratuito incluse", note: "La quota riparte ogni mese." },
        freeOverage: { label: "Foto con download gratuito oltre la quota", note: "Le gallerie continuano a funzionare." },
        video: { label: "Caricamento di video", note: "" },
        address: { label: "Il tuo indirizzo Photohawk", note: "tuonome.photohawk.com" },
        customDomain: { label: "Dominio personalizzato", note: "Le tue gallerie al tuo indirizzo web." },
        pages: { label: "Pagine", note: "Le tue pagine accanto alle tue gallerie." },
        emails: { label: "Modelli email personalizzati", note: "Modifica le email che gli acquirenti ricevono da ogni galleria." },
        logo: { label: "Il tuo logo e i tuoi colori", note: "Nelle tue gallerie, pagine ed email." },
        noBadge: { label: "Nessun badge “Powered by Photohawk”", note: "Con Lite, un piccolo badge compare in fondo alle tue gallerie e alla tua pagina di accesso." },
        live: { label: "Visitatori in tempo reale", note: "Quante persone stanno guardando una galleria in questo momento." },
        analytics: { label: "Statistiche visitatori", note: "Utenti e visite nel tempo, galleria per galleria." },
        sales: { label: "Ordini e vendite nel tempo", note: "" },
        collectionDownloads: { label: "Download per collezione", note: "" },
        people: { label: "Persone nella tua area di lavoro", note: "Contano anche i collaboratori delle gallerie. Chi è già nella tua area di lavoro mantiene l'accesso." },
        roles: { label: "Ruoli", note: "Accesso come proprietario, amministratore, solo caricamento o solo vendite." },
        collaborators: { label: "Collaboratori delle gallerie", note: "" },
        webUploader: { label: "Uploader web", note: "Invia molte foto alla volta e riprende da solo." },
        previewsFirst: { label: "Anteprima prima", note: "Le gallerie vanno online in pochi minuti con anteprime leggere mentre arrivano gli originali." },
        soldFirst: { label: "Prima le foto vendute", note: "Una foto acquistata o scaricata prima che arrivi il suo originale passa avanti." },
        originalsBy: { label: "Data “Originali entro” per gli acquirenti", note: "Fai sapere agli acquirenti quando saranno pronti gli originali." },
        ftp: { label: "Server FTP su richiesta", note: "Se l'FTP è indispensabile: il tuo server, avviato dall'uploader web. Si spegne automaticamente dopo 1 ora di inattività." },
        cloudImport: { label: "Importazione da Dropbox e Google Drive", note: "" },
        studioApp: { label: "App Photohawk Studio per Mac, iPhone e iPad", note: "Oggi Studio funziona nel browser." },
        included: { label: "Spazio incluso", note: "" },
        packs: { label: "Spazio aggiuntivo", note: "Aggiunto in automatico con una carta registrata, fatturato come la combinazione più economica." },
        you: { label: "Assistenza per la tua area di lavoro", note: "" },
        buyers: { label: "Photohawk risponde ai tuoi acquirenti", note: "Le domande degli acquirenti arrivano all'assistenza Photohawk, a meno che tu non imposti un tuo indirizzo." },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} al mese",
        upToPeople: "Fino a {n}",
        unlimited: "Illimitati",
        upToDomains: "Fino a {n}",
        months: "{n} mesi",
        fromNovember: "Da novembre 2026",
        packs: "Da ${usd} ogni {gb} GB al mese",
        overage: "${usd} ogni 1000",
        supportHelp: "Centro assistenza",
        supportEmail: "Email",
        supportPriority: "Prioritaria",
      },
    },
    faq: {
      heading: "Domande",
      items: [
        {
          q: "Le commissioni sulle carte sono incluse?",
          a: "No. Le commissioni sulle carte le addebita il fornitore dei pagamenti, esattamente come oggi: Stripe sul tuo account, oppure vengono trattenute a tariffe locali basate sui costi dove Photohawk elabora il pagamento per te. La nostra commissione (16% con Lite, 14% con Pro, 12% con Max) è l'unico costo Photohawk.",
        },
        {
          q: "La ricerca con selfie è inclusa?",
          a: "Sì, con ogni piano, anche Lite. Lo sono anche la ricerca per pettorale e per orario, la protezione delle anteprime e l'uploader web. L'app Photohawk Studio per Mac, iPhone e iPad arriva in ogni piano quando sarà lanciata, a novembre 2026.",
        },
        {
          q: "Posso cambiare piano in qualsiasi momento?",
          a: "Sì. I piani sono mensili, senza contratto annuale. Quando passi a un piano con una commissione più bassa, questa si applica dal tuo primo mese intero con quel piano. I passaggi a un piano inferiore hanno effetto a fine mese.",
        },
        {
          q: "Ho un accordo personalizzato. Cambia qualcosa?",
          a: "No. Gli accordi personalizzati esistenti restano validi fino alla loro scadenza.",
        },
        {
          q: "Cosa succede ai download gratuiti oltre la quota?",
          a: "Le tue gallerie con download gratuito continuano a funzionare. Ogni mese il tuo piano include 1000 foto con Lite, 5000 con Pro e 10.000 con Max. Oltre, con ogni piano, si pagano $8 ogni 1000 foto ($0,008 a foto).",
        },
        {
          q: "E se mi serve più spazio?",
          a: "Lite include 150 GB, Pro 1 TB e Max 3 TB. Oltre, con una carta registrata, i pacchetti si aggiungono in automatico: +100 GB per $5, +1 TB per $25 o +5 TB per $100 al mese, fatturati come la combinazione più economica che copre ciò che archivi. Senza una carta registrata, i nuovi caricamenti vanno in pausa finché non ne aggiungi una: non viene eliminato nulla e le tue gallerie continuano a vendere.",
        },
        {
          q: "Cosa ottengo con Max?",
          a: "La commissione più bassa, il 12%, più 3 TB di spazio, 10.000 foto con download gratuito al mese, caricamento di video, membri del team illimitati, statistiche visitatori e assistenza prioritaria.",
        },
        {
          q: "Quante persone possono far parte della mia area di lavoro?",
          a: "Fino a 3 persone con Lite, fino a 10 con Pro e quante ne vuoi con Max. Hai già più persone nella tua area di lavoro? Mantengono l'accesso: il limite vale solo quando inviti qualcuno di nuovo.",
        },
        {
          id: "ftp",
          q: "Posso ancora caricare via FTP?",
          a: "Sì, se l'FTP è indispensabile. L'uploader web ora è il modo standard per caricare, con ogni piano: invia molte foto alla volta, molte più di un programma FTP come FileZilla (2 alla volta di default, e non più di 10), e riprende da solo se la connessione cade. Se ti serve ancora l'FTP, chiunque nella tua area di lavoro, con ogni piano, può avviare un proprio server FTP su richiesta dall'uploader web, con un indirizzo Photohawk personale che non cambia mai; si spegne automaticamente dopo 1 ora di inattività. I server FTP condivisi chiudono il 1° novembre 2026.",
        },
        {
          q: "Photohawk Friends & Family fa parte di un piano?",
          a: "No. Friends & Family, la nostra app per le foto della tua famiglia, è un'app separata, in arrivo, e non fa parte di nessun piano Studio. Gestisci gallerie e caricamenti? Quello è Photohawk Studio: oggi funziona nel browser con ogni piano, e l'app Studio per Mac, iPhone e iPad arriva a novembre 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "I prezzi cambiano il 1° novembre 2026 | Photohawk",
      description:
        "Dal 1° novembre 2026 la commissione scende con ogni piano Photohawk, Pro e Max hanno più spazio e le foto con download gratuito sono incluse ogni mese.",
    },
    hero: {
      badge: "Aggiornamento dei prezzi",
      title: "I prezzi cambiano il 1° novembre 2026.",
      titleHighlight: "Commissioni più basse con ogni piano.",
      subtitle:
        "La commissione scende con ogni piano, Pro e Max hanno più spazio e le foto con download gratuito ora sono incluse ogni mese. Non devi fare nulla.",
      ctaPrimary: "Vedi i nuovi prezzi",
      ctaSecondary: "Confronta il tuo piano",
    },
    why: {
      heading: "Cosa migliora",
      items: [
        {
          id: "rates",
          title: "Commissioni più basse con ogni piano",
          body: "La commissione scende con ogni piano, da 1,5 a 9 punti. Pay as you go e Lite diventano il nuovo Lite, senza canone mensile e al 16%, Pro passa al 14% ed Enterprise, che ora si chiama Max, al 12%.",
        },
        {
          id: "storage",
          title: "Più spazio con ogni piano",
          body: "Lite passa da 100 GB a 150 GB, Pro da 500 GB a 1 TB e Max da 1 TB a 3 TB. I 25 GB di Pay as you go diventano 150 GB con Lite.",
        },
        {
          id: "downloads",
          title: "Foto con download gratuito incluse",
          body: "Oggi ogni foto in una galleria con download gratuito si paga. Dal 1° novembre ne sono incluse da 1000 a 10.000 al mese, poi si pagano $8 ogni 1000: quanto oggi o meno, con ogni piano.",
        },
        {
          id: "simple",
          title: "Prezzi più semplici",
          body: "Tre piani invece di quattro, una commissione per piano e spazio aggiuntivo in pacchetti semplici.",
        },
        {
          id: "order",
          title: "Commissioni di transazione invariate",
          body: "Le commissioni di transazione funzionano esattamente come oggi, separate dalla nostra commissione.",
        },
        {
          id: "deals",
          title: "Il tuo accordo viene rispettato",
          body: "Se hai un accordo personalizzato, resta valido fino alla sua scadenza.",
        },
      ],
      honestHeading: "L'unico prezzo che aumenta",
      honestBody:
        "Enterprise, che ora si chiama Max, costa $49 al mese (prima $44,99) dal tuo primo rinnovo a partire dal 1° novembre. In cambio include 3 TB invece di 1 TB, 10.000 foto con download gratuito al mese e la commissione più bassa, il 12% invece del 13,5%: quando vendi circa $270 al mese, la commissione più bassa copre più che abbondantemente la differenza. Pro scende da $29,99 a $29.",
    },
    when: {
      heading: "Cosa succede il 1° novembre",
      subtitle: "Una data per tutti. La tua area di lavoro passa al nuovo piano in automatico.",
      moves: {
        payg: "Sempre $0 al mese. 16% invece del 25% e 150 GB invece di 25 GB.",
        lite: "Lite resta, e ora è gratuito: niente più $16,99 al mese, 16% invece del 18,5% e più spazio: 150 GB invece di 100 GB. Se ti serve di più, lo spazio aggiuntivo parte da $5 ogni 100 GB al mese. Le tue gallerie mantengono il tuo logo e i tuoi colori, con un piccolo badge “Powered by Photohawk”.",
        pro: "$29 invece di $29,99 al mese, 14% invece del 16% e 1 TB invece di 500 GB.",
        enterprise: "Enterprise si chiama ora Max — stesso piano, nuovo nome, ora con 3 TB e una commissione del 12%. Costa $49 al mese invece di $44,99 dal tuo primo rinnovo a partire dal 1° novembre.",
      },
      points: [
        {
          id: "action",
          title: "Non devi fare nulla",
          body: "La tua area di lavoro passa al nuovo piano il 1° novembre 2026. Gallerie, prezzi e pagamenti proseguono come sempre.",
        },
        {
          id: "month",
          title: "La regola del mese intero",
          body: "Se passi a un piano con una commissione più bassa, questa si applica dal tuo primo mese intero con quel piano. I passaggi a un piano inferiore hanno effetto a fine mese.",
        },
        {
          id: "change",
          title: "Cambiare piano",
          body: "Puoi cambiare piano in qualsiasi momento da Fatturazione nella tua area di lavoro.",
        },
        {
          id: "deals",
          title: "Accordi personalizzati",
          body: "Gli accordi personalizzati esistenti restano validi fino alla loro scadenza.",
        },
        {
          id: "privacy",
          title: "Ricerca con selfie e privacy",
          body: "Dal 1° novembre scegli tu per quanto tempo conservare i dati del volto usati per la ricerca con selfie: nessuna scadenza per impostazione predefinita, o un periodo dopo il caricamento di ogni foto. Le tue foto, le vendite e la ricerca per pettorale non cambiano.",
        },
        {
          id: "team",
          title: "Il tuo team",
          body: "Lite include fino a 3 persone, Pro fino a 10 e Max illimitate. Hai già più persone nella tua area di lavoro? Mantengono l'accesso — il limite vale solo quando aggiungi qualcuno di nuovo.",
        },
      ],
    },
    ftp: {
      date: "1° novembre 2026",
      heading: "L'uploader web ora è il modo standard per caricare",
      body: "I server FTP condivisi sempre attivi chiudono il 1° novembre 2026. L'uploader web è più veloce dell'FTP e sfrutta al massimo la tua connessione, con ogni piano. Se l'FTP è indispensabile, puoi avviare il tuo server su richiesta.",
      items: [
        {
          id: "web",
          title: "L'uploader web, con ogni piano",
          body: "Invia molte foto alla volta, molte più di un programma FTP come FileZilla (2 alla volta di default, e non più di 10), si regola di continuo per sfruttare al massimo la tua banda e, se la connessione cade, riprende da solo. Con ogni piano, le anteprime leggere possono partire per prime, così la tua galleria è online in pochi minuti.",
        },
        {
          id: "ondemand",
          title: "Se l'FTP è indispensabile: con ogni piano",
          body: "Disponibile ora, con ogni piano: avvia il tuo server FTP su richiesta dall'uploader web, con un indirizzo Photohawk personale che non cambia mai. Si spegne automaticamente dopo 1 ora di inattività.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, con ogni piano",
          body: "L'app di caricamento Photohawk per la tua area di lavoro, su desktop e iOS.",
        },
      ],
    },
    privacyLink: "Informativa sulla conservazione dei dati biometrici",
    included: {
      heading: "Cosa include ogni piano",
      everyPlan:
        "Ogni piano: ricerca con selfie, ricerca per pettorale e orario, protezione delle anteprime, l'uploader web con anteprima prima e il tuo server FTP su richiesta.",
      upgrades: "Pro e Max aggiungono le statistiche visitatori e un dominio personalizzato. Max aggiunge il caricamento di video.",
    },
    compare: {
      heading: "Oggi e dal 1° novembre",
      subtitle: "Piano per piano, in dollari USA.",
      rows: {
        fee: "Canone mensile",
        commission: "Commissione sulle vendite",
        storage: "Spazio incluso",
        downloads: "Foto con download gratuito",
        extraStorage: "Spazio aggiuntivo",
      },
      downloadsToday: "{price} l'una",
      downloadsNext: "{count} al mese incluse, poi $8 ogni 1000",
      extraStorageToday: "$0,023 per GB al mese",
      extraStorageNext: "Pacchetti: +100 GB $5, +1 TB $25, +5 TB $100 al mese",
    },
    countries: {
      heading: "Cosa significa dove vendi",
      subtitle: "Esempi nella tua valuta. Scegli un paese.",
      selectLabel: "Paese",
      examplesBadge: "Esempi",
      orderHeading: "Un ordine tipico: {amount}",
      orderSubtitle: "L'ordine medio {inCountry}, pagato con carta. Commissione sulla carta {fee}, {kind}.",
      kind: {
        stripe: "addebitata da Stripe sul tuo account",
        managed: "trattenuta dove Photohawk elabora il pagamento",
      },
      colPlan: "Il tuo piano",
      colCommission: "Commissione Photohawk",
      colKeep: "Ti resta",
      perOrderMore: "+{amount} a ordine",
      monthlyHeading: "Un mese di vendite, tutto compreso",
      monthlySubtitle:
        "Canone, commissione e commissioni sulle carte insieme, come quota delle tue vendite, con il piano che costa meno a ogni livello.",
      level: "{amount} di vendite al mese",
      onPlan: "con {plan}",
      keepMore: "Ti restano circa {amount} in più al mese",
      disclaimer:
        "Solo esempi, basati sui valori medi degli ordini e convertiti a tassi di cambio fissi (ottobre 2026). I tuoi numeri dipendono da ordini, carte e piano. I costi dei download gratuiti e dello spazio non sono inclusi.",
    },
    faq: {
      heading: "Domande",
      items: [
        {
          q: "Devo fare qualcosa?",
          a: "No. Il 1° novembre 2026 la tua area di lavoro passa al nuovo piano in automatico. Gallerie, prezzi e pagamenti proseguono come sempre.",
        },
        {
          q: "Ho Lite. Cosa succede?",
          a: "Lite resta, e ora è gratuito: dal 1° novembre niente più $16,99 al mese, la tua commissione è il 16% invece del 18,5% e hai più spazio: 150 GB invece di 100 GB. Se archivi di più, lo spazio aggiuntivo parte da $5 ogni 100 GB al mese e si aggiunge in automatico se hai una carta registrata (senza, i nuovi caricamenti vanno in pausa finché non aggiungi una carta; non viene eliminato nulla). Le tue gallerie mantengono il tuo logo e i tuoi colori, con un piccolo badge “Powered by Photohawk”.",
        },
        {
          q: "E se archivio più di quanto include il mio piano?",
          a: "Con una carta registrata, i pacchetti di spazio si aggiungono in automatico, da $5 ogni 100 GB al mese, fatturati come la combinazione più economica che copre ciò che archivi. Senza una carta registrata, i nuovi caricamenti vanno in pausa finché non ne aggiungi una: non viene eliminato nulla e le tue gallerie continuano a vendere.",
        },
        {
          q: "Ho Enterprise. Cosa succede?",
          a: "Enterprise si chiama ora Max: stesso piano, nuovo nome. Dal 1° novembre la tua commissione è il 12% invece del 13,5% e hai 3 TB invece di 1 TB. Il nuovo prezzo, $49 al mese invece di $44,99, si applica dal tuo primo rinnovo a partire dal 1° novembre.",
        },
        {
          q: "Quante persone possono far parte della mia area di lavoro?",
          a: "Fino a 3 persone con Lite, fino a 10 con Pro e quante ne vuoi con Max. Hai già più persone nella tua area di lavoro? Mantengono l'accesso: il limite vale solo quando inviti qualcuno di nuovo.",
        },
        {
          id: "ftp",
          q: "Cosa succede all'FTP?",
          a: "I server FTP condivisi sempre attivi chiudono il 1° novembre 2026 e l'uploader web ora è il modo standard per caricare, con ogni piano. Se l'FTP è indispensabile, chiunque nella tua area di lavoro, con ogni piano, può già avviare un proprio server FTP su richiesta dall'uploader web, con un indirizzo Photohawk personale che non cambia mai; si spegne automaticamente dopo 1 ora di inattività.",
        },
        {
          q: "Il mio accordo personalizzato cambierà?",
          a: "No. Gli accordi personalizzati esistenti restano validi fino alla loro scadenza.",
        },
        {
          q: "Se cambio piano, da quando vale la nuova commissione?",
          a: "La commissione più bassa di un piano si applica dal tuo primo mese intero con quel piano. I passaggi a un piano inferiore hanno effetto a fine mese.",
        },
        {
          q: "Le commissioni sulle carte cambiano?",
          a: "No. Le commissioni sulle carte e di elaborazione dei pagamenti funzionano esattamente come oggi, separate dalla nostra commissione.",
        },
        {
          q: "Photohawk Friends & Family fa parte di un piano?",
          a: "No. Friends & Family, la nostra app per le foto della tua famiglia, è un'app separata, in arrivo, e non fa parte di nessun piano Studio. Gestisci gallerie e caricamenti? Quello è Photohawk Studio: oggi funziona nel browser con ogni piano, e l'app Studio per Mac, iPhone e iPad arriva a novembre 2026.",
        },
      ],
    },
    cta: {
      heading: "Tutti i nuovi prezzi",
      body: "Piani, spazio, download gratuiti e commissioni sulle carte per paese, tutto in una pagina.",
      button: "Vedi i nuovi prezzi",
    },
  },

  app: {
    badge: "In arrivo",
    platform: "Prima su iPhone",
    heading: "Photohawk Friends & Family, per le foto della tua famiglia",
    intro: "Friends & Family è la nostra app per tutte le foto della tua famiglia: quelle che scatti e quelle che condividi. È un'app separata, in arrivo, e non fa parte dei piani Studio.",
    points: [
      { id: "private", title: "Trova le foto della tua famiglia, in privato", body: "Ordina e trova le foto delle persone che ami, direttamente sul telefono." },
      { id: "albums", title: "Album in cui ognuno vede le proprie foto", body: "Condividi un album e ognuno vede solo le foto in cui compare, abbinate con un selfie: la stessa tecnologia delle gallerie Photohawk." },
      { id: "event", title: "Raccogli le foto di tutti da un evento", body: "Riunisci in un unico posto le foto scattate da amici e parenti." },
      { id: "prints", title: "Stampe e fotolibri", body: "Trasforma le più belle in stampe e fotolibri." },
    ],
    cta: "Avvisami",
    ctaNote: "La lista d'attesa apre presto.",
    phoneAlbum: "Solo io",
    phoneAlbumCount: "12 foto",
  },
};

export default it;
